import { InspirationRecommendWords } from "@/components/InspirationRecommendWords";
import type { DisplayedModule, OperationType } from "@/types/inspirationWords";
import React, { useCallback } from "react";

/**
 * Hook: 灵感词卡片渲染器
 *
 * 职责：
 * - 提供自定义渲染逻辑
 * - 决定是否显示灵感词
 * - 为卡片注入 recordOperation 回调
 * - 渲染灵感词组件
 *
 * @param displayedModules - 已显示的灵感词模块 Map
 * @param recordOperation - 记录操作的回调函数
 * @param isDetailPageOpen - 是否在详情页中
 * @returns 自定义渲染函数
 */
export function useInspirationCardRenderer(
  displayedModules: Map<string, DisplayedModule>,
  recordOperation: (fileId: string, type: OperationType) => void,
  isDetailPageOpen: boolean
) {
  return useCallback(
    (record: any, index: number, defaultRender: () => React.ReactElement) => {
      const defaultCard = defaultRender();
      const cardWidth = defaultCard.props?.width;
      const cardWithHandlers = React.cloneElement(defaultCard, {
        recordOperation,
      });

      // 判断是否应该显示灵感词
      const hasInspirationWords =
        record?.file_id &&
        !record.type &&
        !record.folding_num &&
        displayedModules.has(String(record.file_id));

      const shouldShowInspirationWords =
        hasInspirationWords && !isDetailPageOpen;

      if (!shouldShowInspirationWords) {
        return cardWithHandlers;
      }

      const inspirationModule = displayedModules.get(String(record.file_id))!;

      return (
        <div>
          {cardWithHandlers}
          <InspirationRecommendWords
            words={inspirationModule.words}
            relatedFileId={String(record.file_id)}
            moduleLocation={index + 1}
            triggerType={inspirationModule.triggerType}
            sourceFileIds={inspirationModule.sourceFileIds}
            style={{ width: cardWidth }}
          />
        </div>
      );
    },
    [displayedModules, recordOperation, isDetailPageOpen]
  );
}
